// Candidate portal API client. All calls hit /candidate/api/* and rely on
// the candidate_session_token HttpOnly cookie for auth, which is completely
// separate from the agency session cookie — these two auth surfaces cannot
// collide. HttpOnly keeps the session opaque to JS so XSS cannot steal it;
// idle auto-logout in CandidateLayout provides the inactivity protection.

export interface CandidateProfile {
  candidateid: number
  agencyid: number
  email: string
  information: Record<string, any>
  cv: {
    has_cv: boolean
    filename: string | null
  }
}

export interface CandidateAgencyOption {
  candidateid: number
  agencyid: number
  agency_name: string
}

export interface CandidateMeSummary {
  candidateid: number
  agencyid: number
  agency_name?: string | null
  email: string
  full_name?: string
  phone?: string
  available_for_work?: boolean
  work_preferences?: Record<string, any>
  has_cv?: boolean
  // Every agency this candidate's email can switch between
  agencies?: CandidateAgencyOption[]
}

export interface PendingTokenResponse {
  success: true
  pending_token: string
  masked_email: string
  message?: string
}

export interface ErrorResponse {
  error: string
}

export interface VerifyResponse {
  success: true
  candidate?: CandidateMeSummary
  message?: string
  // Present when the email belongs to more than one agency
  requires_agency_selection?: boolean
  select_token?: string
  agencies?: CandidateAgencyOption[]
}

export interface WorkPreferences {
  preferred_roles?: string[]
  preferred_locations?: string[]
  preferred_industries?: string[]
  job_types?: string[]
  remote_preference?: string
  min_salary?: string | number
  max_salary?: string | number
  currency?: string
  willing_to_travel?: boolean
  notes?: string
}

export interface AvailabilityPayload {
  available_for_work?: boolean
  available_from_date?: string
  availability_notes?: string
  notice_period_weeks?: number
}

export interface CandidateChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface CandidateChatResponse {
  response: string
  messages: CandidateChatMessage[]
}

export interface ThreadMessage {
  messageid: number
  sender_type: 'candidate' | 'agency' | 'bot'
  sender_name: string
  body: string
  created_at: string
  read_by_candidate_at: string | null
  read_by_agency_at: string | null
}

export interface ThreadResponse {
  messages: ThreadMessage[]
  unread_count?: number
}

const BASE = '/candidate/api'

async function jsonRequest<T>(
  path: string,
  method: string = 'GET',
  body?: unknown,
): Promise<T | ErrorResponse> {
  try {
    const options: RequestInit = {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
    if (body !== undefined) {
      options.body = JSON.stringify(body)
    }
    const res = await fetch(`${BASE}${path}`, options)
    const data = (await res.json().catch(() => ({}))) as T | ErrorResponse
    if (!res.ok) {
      const err = (data as ErrorResponse).error || `HTTP ${res.status}`
      return { error: err }
    }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' }
  }
}

// ----- auth -----

export const candidateRegister = (agencyid: number, email: string, full_name: string, phone: string, first_name?: string, last_name?: string) =>
  jsonRequest<PendingTokenResponse>('/register', 'POST', { agencyid, email, full_name, phone, first_name, last_name })

export const candidateVerifyRegister = (pending_token: string, code: string, password: string) =>
  jsonRequest<VerifyResponse>('/verify-register', 'POST', { pending_token, code, password })

export const candidateLogin = (email: string, password: string) =>
  jsonRequest<PendingTokenResponse>('/login', 'POST', { email, password })

export const candidateVerifyLogin = (pending_token: string, code: string) =>
  jsonRequest<VerifyResponse>('/verify-login', 'POST', { pending_token, code })

// Only used when the email belongs to more than one agency
export const candidateSelectAgency = (select_token: string, agencyid: number) =>
  jsonRequest<VerifyResponse>('/select-agency', 'POST', { select_token, agencyid })

// Switch the active agency of a logged-in candidate (profile switcher)
export const candidateSwitchAgency = (agencyid: number) =>
  jsonRequest<VerifyResponse>('/switch-agency', 'POST', { agencyid })

export const candidateLogout = () =>
  jsonRequest<{ message: string }>('/logout', 'POST')

export const candidateDeleteAccount = () =>
  jsonRequest<{ message: string }>('/account', 'DELETE')

export const candidateGetMe = () =>
  jsonRequest<{ candidate: CandidateMeSummary }>('/me', 'GET')

export const candidateForgotPassword = (email: string) =>
  jsonRequest<PendingTokenResponse>('/forgot-password', 'POST', { email })

export const candidateResetPassword = (pending_token: string, code: string, new_password: string) =>
  jsonRequest<{ success: boolean; message: string }>('/reset-password', 'POST', {
    pending_token,
    code,
    new_password,
  })

// ----- profile / preferences / availability -----

export const candidateGetProfile = () =>
  jsonRequest<CandidateProfile>('/profile', 'GET')

export interface ResumeScore {
  score: number
  breakdown: Record<string, { earned: number; max: number }>
}

export const candidateGetScore = () =>
  jsonRequest<ResumeScore>('/score', 'GET')

export const candidatePatchProfile = (updates: Record<string, any>) =>
  jsonRequest<{ success: boolean; information: Record<string, any> }>('/profile', 'PATCH', updates)

export const candidateSetAvailability = (payload: AvailabilityPayload) =>
  jsonRequest<{ success: boolean } & AvailabilityPayload>('/availability', 'PUT', payload)

export const candidateSetPreferences = (payload: WorkPreferences) =>
  jsonRequest<{ success: boolean; work_preferences: WorkPreferences }>('/preferences', 'PUT', payload)

// ----- CV -----

export const candidateGetCvMeta = () =>
  jsonRequest<{ has_cv: boolean; filename: string | null }>('/cv', 'GET')

export async function candidateUploadCv(file: File): Promise<
  | { success: true; filename: string; extracted_fields: Record<string, any>; information: Record<string, any> }
  | ErrorResponse
> {
  try {
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch(`${BASE}/cv`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      return { error: data.error || `HTTP ${res.status}` }
    }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Upload failed' }
  }
}

export const candidateCvDownloadUrl = () => `${BASE}/cv/download`

// ----- chat wizard -----

export const candidateChat = (messages: CandidateChatMessage[]) =>
  jsonRequest<CandidateChatResponse>('/chat', 'POST', { messages })

// ----- messaging -----

export const candidateGetMessages = () =>
  jsonRequest<ThreadResponse>('/messages', 'GET')

export const candidateSendMessage = (body: string) =>
  jsonRequest<{ success: boolean; messages: ThreadMessage[] }>('/messages', 'POST', { body })

export async function candidateUnreadMessageCount(): Promise<number> {
  const res = await jsonRequest<{ unread_count: number }>('/messages/unread-count', 'GET')
  if ('error' in res) return 0
  return res.unread_count ?? 0
}

// ----- interviews -----

export interface CandidateInterview {
  interviewid: number
  applicationid: number
  jobid: number | null
  job_title: string
  application_status: string
  interview_date: string
  interview_time: string
  interview_type: string
  interviewer_name: string
  location: string
  notes: string
  outcome: string
  created_at: string
}

export const candidateGetInterviews = () =>
  jsonRequest<{ interviews: CandidateInterview[] }>('/interviews', 'GET')

// ----- public (no auth required) -----

export async function candidateAgencyInfo(agencyid: number): Promise<
  { agencyid: number; name: string } | ErrorResponse
> {
  try {
    const res = await fetch(`${BASE}/agency-info?agencyid=${encodeURIComponent(agencyid)}`, {
      credentials: 'include',
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return { error: data.error || `HTTP ${res.status}` }
    return data
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Network error' }
  }
}
